import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { get } from 'lodash';
import { Box, Icon, IconButton, Tooltip, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import * as api from '_core/utils/api';
import { useGlobal } from '_core/hooks/useGlobal';
import { withVisitorInfo } from '_core/utils/visitorInfo';
import usePromise from '_core/hooks/usePromise';
import { withLang } from '_core/hocs/withLang';
import { UserAvatar } from '_core/components/UserAvatar';
import { sidebarUserStyle } from './sidebarUserStyle';

const getTextFromFields = (userInfo, fields) =>
  fields
    .map((field) => get(userInfo, field))
    .filter(Boolean)
    .join(' ');

export const RawSidebarUser = (props) => {
  const {
    classes,
    isMini,
    hasTooltip,
    onProfileOpen,
    majorFields,
    majorTooltipFields,
    minorFields,
    minorTooltipFields,
    editBesideMinor,
    visitorInfo,
    getLangString: l
  } = props;

  const statusTypes = usePromise(api.getClientVisitorStatusTypes, []);
  const statuses = usePromise(api.getClassicVisitorStatus, []);
  const {
    config: { DISABLE_SHOW_PROFILE }
  } = useGlobal();
  const canShowProfile = !DISABLE_SHOW_PROFILE;

  const rootClasses = clsx({
    [classes.root]: true,
    [classes.rootMini]: isMini
  });

  const avatarClasses = clsx({
    [classes.avatar]: true,
    [classes.avatarMini]: isMini
  });

  const editClasses = clsx({
    [classes.edit]: true,
    [classes.editMini]: isMini
  });

  const renderStatus = ({ id, name, title, is_query_info, visitor_group }) => {
    if (
      !is_query_info ||
      !visitorInfo.info?.status ||
      !visitorInfo.inGroup(visitor_group)
    )
      return null;

    var status = statuses.data.find(
      (item) => item.type === id && item.id === visitorInfo.info.status[name]
    );

    if (!status) return null;

    return (
      <Tooltip key={name} title={title + ': ' + l(status.title)}>
        <Typography variant="caption" noWrap={true}>
          {l(status.title)}
        </Typography>
      </Tooltip>
    );
  };

  const minorText = getTextFromFields(visitorInfo.info, minorFields);

  const editButton =
    canShowProfile ? (
      <Tooltip title={l('r._.header.editprofile')} placement="right">
        <IconButton
          size="small"
          onClick={onProfileOpen}
          className={editClasses}
        >
          <Icon fontSize="small">edit</Icon>
        </IconButton>
      </Tooltip>
    ) : null;

  return (
    <Box display="flex" alignItems="center" className={rootClasses}>
      <UserAvatar
        className={avatarClasses}
        onClick={canShowProfile ? onProfileOpen : undefined}
        disableClick={!canShowProfile}
        tooltip={canShowProfile ? l('r._.header.editprofile') : undefined}
        tooltipProps={{
          disableFocusListener: !hasTooltip || !canShowProfile,
          disableHoverListener: !hasTooltip || !canShowProfile,
          disableTouchListener: !hasTooltip || !canShowProfile,
          placement: 'right',
          enterDelay: 0
        }}
      />
      {minorText ? (
        <Tooltip title={getTextFromFields(visitorInfo.info, minorTooltipFields)}>
          <Typography className={classes.login} noWrap={true}>
            {minorText}
          </Typography>
        </Tooltip>
      ) : null}
      {editBesideMinor ? (
        <Box className={classes.editBesideMinor}>{editButton}</Box>
      ) : null}

      <Box display="flex" alignItems="center" className={classes.textWrap}>
        <Box display="flex" flexDirection="column" className={classes.text}>
          <Tooltip
            title={getTextFromFields(visitorInfo.info, majorTooltipFields)}
          >
            <Typography className={classes.login} noWrap={true}>
              {getTextFromFields(visitorInfo.info, majorFields)}
            </Typography>
          </Tooltip>

          {statusTypes?.data && statuses?.data
            ? statusTypes.data.map(renderStatus)
            : null}
        </Box>

        {editBesideMinor ? null : editButton}
      </Box>
    </Box>
  );
};

RawSidebarUser.defaultProps = {
  minorFields: [''],
  minorTooltipFields: [''],
  majorFields: ['login'],
  majorTooltipFields: ['login'],
  editBesideMinor: false
};

RawSidebarUser.propTypes = {
  // self props
  isMini: PropTypes.bool,
  hasTooltip: PropTypes.bool,
  onProfileOpen: PropTypes.func.isRequired,
  minorFields: PropTypes.arrayOf(PropTypes.string),
  minorTooltipFields: PropTypes.arrayOf(PropTypes.string),
  majorFields: PropTypes.arrayOf(PropTypes.string),
  majorTooltipFields: PropTypes.arrayOf(PropTypes.string),
  editBesideMinor: PropTypes.bool,

  // withVisitorInfo
  visitorInfo: PropTypes.object.isRequired,

  // `withStyles` HOC props
  classes: PropTypes.object.isRequired,

  // `withLang` HOC props
  langInfo: PropTypes.object,
  langStrings: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired
};

export const SidebarUser = withVisitorInfo(
  withLang(
    withStyles(sidebarUserStyle, { name: 'RtsSidebarUser' })(RawSidebarUser)
  )
);
